<script setup lang="ts">
import { computed, useId } from 'vue'

/**
 * La taza de café animada (chorro, crema, vapor). Solo el dibujo: la usan el
 * loader del dashboard y la bienvenida del login.
 *
 *   <TazaCafe :progreso="0.4" />     nivel fijo, sube con transición
 *   <TazaCafe :progreso="null" />    sube y baja suave (sin avance conocido)
 */
const props = withDefaults(
  defineProps<{
    progreso?: number | null
    /** Segundos que tarda el café en llegar a un nivel nuevo. */
    duracion?: number
  }>(),
  { progreso: null, duracion: 0.7 },
)

// Ids propios: dos tazas a la vez (bienvenida y dashboard) no comparten clipPath.
const id = useId()
const idTaza = `${id}-taza`
const idChorro = `${id}-chorro`
const idLiquido = `${id}-liquido`

const indeterminado = computed(() => props.progreso === null)
const avance = computed(() => Math.min(1, Math.max(0, props.progreso ?? 0)))
const lleno = computed(() => avance.value >= 1)

// Fondo de la taza en y=96 y borde en y=46: el café sube hasta y=51 como máximo.
// Arranca con un poco de café para que la taza nunca se vea vacía.
const nivel = computed(() => 96 - (0.12 + avance.value * 0.88) * 45)

const TAZA = 'M28 46 H84 V68 C84 85 74 96 61 96 H51 C38 96 28 85 28 68 Z'
</script>

<template>
  <svg viewBox="0 0 120 120" aria-hidden="true">
    <defs>
      <clipPath :id="idTaza">
        <path :d="TAZA" />
      </clipPath>
      <linearGradient :id="idChorro" x1="0" y1="0" x2="0" y2="34" gradientUnits="userSpaceOnUse">
        <stop offset="0" stop-color="#6b4428" stop-opacity="0" />
        <stop offset="1" stop-color="#6b4428" />
      </linearGradient>
      <linearGradient :id="idLiquido" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stop-color="#7a4f2e" />
        <stop offset="1" stop-color="#3b2416" />
      </linearGradient>
    </defs>

    <!-- Plato -->
    <ellipse cx="57" cy="101" rx="40" ry="5" class="plato" />

    <!-- Vapor -->
    <g class="vapor-grupo" :class="{ fuerte: lleno }">
      <path class="vapor" d="M42 40 c-4 -5 4 -8 0 -13 c-4 -5 4 -8 0 -13" />
      <path class="vapor vapor-2" d="M70 40 c-4 -5 4 -8 0 -13 c-4 -5 4 -8 0 -13" />
      <path class="vapor vapor-3" d="M56 38 c-4 -5 4 -8 0 -13 c-4 -5 4 -8 0 -13" :class="{ oculto: !lleno }" />
    </g>

    <!-- Interior de la taza -->
    <path :d="TAZA" class="taza-fondo" />

    <!-- Chorro de espresso: detrás del café, así desaparece al tocar la superficie -->
    <g class="chorro" :class="{ oculto: lleno }">
      <line x1="56" y1="4" x2="56" y2="94" :stroke="`url(#${idChorro})`" class="chorro-base" />
      <line x1="56" y1="4" x2="56" y2="94" class="chorro-brillo" />
    </g>

    <!-- Café con crema -->
    <g :clip-path="`url(#${idTaza})`">
      <g
        class="liquido"
        :class="{ vaiven: indeterminado }"
        :style="indeterminado ? undefined : { transform: `translateY(${nivel}px)`, transitionDuration: `${duracion}s` }"
      >
        <path class="ola ola-crema" d="M0 0 q5 -3 10 0 t10 0 t10 0 t10 0 t10 0 t10 0 t10 0 t10 0 t10 0 t10 0 t10 0 t10 0 t10 0 t10 0 V60 H0 Z" />
        <path class="ola" d="M0 2.5 q5 -2.5 10 0 t10 0 t10 0 t10 0 t10 0 t10 0 t10 0 t10 0 t10 0 t10 0 t10 0 t10 0 t10 0 t10 0 V60 H0 Z" :fill="`url(#${idLiquido})`" />
      </g>
    </g>

    <!-- Borde y asa -->
    <path :d="TAZA" class="taza-borde" />
    <path d="M84 53 h5 a10 10 0 0 1 0 20 h-6" class="taza-borde" />
    <path d="M33 52 V66" class="brillo" />
  </svg>
</template>

<style scoped>
.plato {
  fill: rgb(var(--c-fg) / 0.07);
}
.taza-fondo {
  fill: rgb(var(--c-bg-muted));
}
.taza-borde {
  fill: none;
  stroke: rgb(var(--c-fg-subtle));
  stroke-width: 3.5;
  stroke-linecap: round;
  stroke-linejoin: round;
}
.brillo {
  stroke: rgb(255 255 255 / 0.35);
  stroke-width: 2.5;
  stroke-linecap: round;
}

.liquido {
  transition: transform 0.7s cubic-bezier(0.33, 1, 0.68, 1);
}
.liquido.vaiven {
  animation: vaiven 2.6s ease-in-out infinite;
}
.ola {
  animation: ola 1.6s linear infinite;
}
.ola-crema {
  fill: #c8a97e;
  animation-duration: 2.4s;
  animation-direction: reverse;
}

.chorro {
  transition: opacity 0.4s ease;
}
.chorro-base {
  stroke-width: 2.6;
  stroke-linecap: round;
}
.chorro-brillo {
  stroke: rgb(255 255 255 / 0.35);
  stroke-width: 1;
  stroke-linecap: round;
  stroke-dasharray: 2 10;
  animation: chorro 0.45s linear infinite;
}

.vapor {
  fill: none;
  stroke: rgb(var(--c-fg-subtle) / 0.55);
  stroke-width: 2.2;
  stroke-linecap: round;
  opacity: 0;
  animation: vapor 3s ease-out infinite;
  transition: opacity 0.4s ease;
}
.vapor-2 {
  animation-delay: 1s;
}
.vapor-3 {
  animation-delay: 2s;
}
.vapor-grupo.fuerte .vapor {
  stroke: rgb(var(--c-accent) / 0.7);
}
.oculto {
  opacity: 0 !important;
  animation-play-state: paused;
}

@keyframes ola {
  to {
    transform: translateX(-20px);
  }
}
@keyframes chorro {
  to {
    stroke-dashoffset: -12;
  }
}
@keyframes vapor {
  0% {
    opacity: 0;
    transform: translateY(4px);
  }
  30% {
    opacity: 0.9;
  }
  100% {
    opacity: 0;
    transform: translateY(-10px);
  }
}
@keyframes vaiven {
  0%,
  100% {
    transform: translateY(78px);
  }
  50% {
    transform: translateY(60px);
  }
}

@media (prefers-reduced-motion: reduce) {
  .ola,
  .chorro-brillo,
  .vapor,
  .liquido.vaiven {
    animation: none;
  }
  .vapor {
    opacity: 0.5;
  }
}
</style>
